import React from 'react';
import { MoldOrder, MoldOrderStatus, MoldOrderType, Mold } from '../../../types/mold';
import { Eye } from 'lucide-react';
import MoldOrderStatusComponent from '../../shared/MoldOrderStatus';
import { useNavigate } from 'react-router-dom';
import { getPriorityLabel } from '../../MoldDetail/utils';
import Badge from '../../shared/Badge';
import { getApprovalTechnician, getAssignedTechnician, getSupervisorReviewDate, getSupervisorReviewDateString, getMachineNumber, getOperationDate, getOperationDateString, getOperationDuration, getOperationDurationString, getReporterConfirmLabel, getReporterConfirmationDate, getReporterConfirmationDateString, getRejectedReason, getRejectedPerson, getRejectedState } from '../../utils/moldOrder';
import { formatShortThaiDateFromString } from '../../../utils/dateUtils';
import DateLabel from '../../shared/DateLabel';
import { ClientCompanyCode } from '../../../models/appState';
import { applyMoldOrderColumnConfiguration } from './columnConfigurations';
import { getMoldTableColumns } from '../../MoldTableView';

export interface Column {
  key: keyof MoldOrder | 'actions' | 'productParts';
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (order: MoldOrder) => React.ReactNode;
  filterOptions?: (orders: MoldOrder[]) => string[];
  getFilterValue?: (order: MoldOrder) => string;
}

export interface GenericColumn<T> {
  key: string;
  label: string;
  group?: string;
  sortable?: boolean;
  filterable?: boolean;
  isDateColumn?: boolean;
  render?: (object: T) => React.ReactNode;
  filterOptions?: (objects: T[]) => string[];
  getFilterValue?: (object: T) => string | Date | null;
  sortFunction?: (a: T, b: T) => number;
  toCSV?: (object: T) => string;
}

export const ActionCell: React.FC<{
  id: string,
  prefixUrl: string,
  suffixUrl?: string
}> = ({ id, prefixUrl, suffixUrl }) => {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(`/${prefixUrl}/${id}${suffixUrl ?? ''}`)}
      className="flex items-center justify-center px-4 py-2 text-sm text-gray-700 w-full hover:bg-gray-100 transition-colors duration-200"
    >
      <Eye
        size={16}
        className="text-gray-500 group-hover:text-blue-500 transition-colors duration-200"
      />
    </button>
  );
};

export const getMoldOrdersTableColumns = ({
  statusFilterable = false,
  getStatusText,
  getMoldOrderTypeLabel,
  companyCode,
}: { 
  statusFilterable?: boolean;
  getStatusText?: (status: MoldOrderStatus, type?: MoldOrderType) => string;
  getMoldOrderTypeLabel?: (type: MoldOrderType) => string;
  companyCode?: ClientCompanyCode;
} = {}): GenericColumn<MoldOrder>[] => {
  // Fallback functions when hooks are not available
  const safeGetStatusText = getStatusText || ((status: MoldOrderStatus) => status);
  const safeGetMoldOrderTypeLabel = getMoldOrderTypeLabel || ((orderType: MoldOrderType) => orderType);
  
  const allColumns: GenericColumn<MoldOrder>[] = [
    {
      key: 'id',
      label: 'เลขที่ใบงาน',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      render: (order) => `#${order.id}`,
      filterOptions: (orders) => [...new Set(orders.map(order => `#${order.id}`))],
      getFilterValue: (order) => `#${order.id}`,
      toCSV: (order) => `${order.id}`,
    },
    {
      key: 'mold',
      label: 'แม่พิมพ์',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          <div className="text-sm text-gray-500">{order.mold.sbiId}</div>
        </div>
      ),
      filterOptions: (orders) => [...new Set(orders.map(order => order.mold.sbiId))],
      getFilterValue: (order) => order.mold.sbiId,
      sortFunction: (a, b) => {
        const aValue = a.mold.sbiId;
        const bValue = b.mold.sbiId;
        if (aValue === bValue) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        return aValue.localeCompare(bValue);
      },
      toCSV: (order) => order.mold.sbiId,
    },
    {
      key: 'moldName',
      label: 'Mold Name',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          <div className="text-sm text-gray-500">{order.mold.moldName ?? '-'}</div>
        </div>
      ),
      filterOptions: (orders) => [...new Set(orders.map(order => order.mold.moldName))],
      getFilterValue: (order) => order.mold.moldName,
      toCSV: (order) => order.mold.moldName,
    },
    {
      key: 'moldCode',
      label: 'Mold Code',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          <div className="text-sm text-gray-500">{order.mold.moldCode ?? '-'}</div>
        </div>
      ),
      filterOptions: (orders) => [...new Set(orders.map(order => order.mold.moldCode))],
      getFilterValue: (order) => order.mold.moldCode,
      toCSV: (order) => order.mold.moldCode,
    },
    {
      key: 'moldNumber',
      label: 'แม่พิมพ์ลูกที่',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          {order.mold.moldNumber}
        </div>
      ),
      filterOptions: (orders) => [...new Set(
        orders
          .map(order => {
            return order.mold.moldNumber
          })
      )],
      getFilterValue: (order) => order.mold.moldNumber,
      toCSV: (order) => order.mold.moldNumber,
    },
    {
      key: 'productParts',
      label: 'รหัสชิ้นงาน',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      // Part numbers are identifiers, not statuses — render as plain stacked text, not pills
      // (Badge rule: badges are for status/category/priority only).
      render: (order) => (
        <div className="flex flex-col gap-0.5 text-sm text-gray-700">
          {order.mold.productParts.map((part, i) => (
            <span key={`${part.partNumber}-${i}`} className="whitespace-nowrap">
              {part.partNumber}
            </span>
          ))}
        </div>
      ),
      filterOptions: (orders) => {
        const parts = orders.flatMap(order =>
          order.mold.productParts.map(part => part.partNumber)
        );
        return [...new Set(parts)];
      },
      getFilterValue: (order) => order.mold.productParts.map(p => p.partNumber).join(','),
      toCSV: (order) => `"${order.mold.productParts.map(p => p.partNumber.replace(/"/g, '""')).join(', ')}"`,
    },
    {
      key: 'customer',
      label: 'ลูกค้า',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      // Customer code is data, not a status — plain text, not a pill (Badge rule).
      render: (order) => (
        <span className="text-sm text-gray-700">{order.mold.customer.code}</span>
      ),
      filterOptions: (orders) => [...new Set(orders.map(order => order.mold.customer.code))],
      getFilterValue: (order) => order.mold.customer.code,
      toCSV: (order) => order.mold.customer.code,
    },
    {
      key: 'type',
      label: 'ประเภทใบงาน',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      render: (order) => {
        const typeLabel = safeGetMoldOrderTypeLabel(order.type);
        return <span>{typeLabel}</span>;
      },
      filterOptions: (orders) => [...new Set(orders.map(order => safeGetMoldOrderTypeLabel(order.type)))],
      getFilterValue: (order) => safeGetMoldOrderTypeLabel(order.type),
      toCSV: (order) => safeGetMoldOrderTypeLabel(order.type),
    },
    {
      key: 'status',
      label: 'สถานะ',
      group: 'สถานะและการจัดการ',
      sortable: true,
      filterable: statusFilterable,
      render: (order) => {
        return (
          <MoldOrderStatusComponent moldOrder={order} />
        );
      },
      filterOptions: (orders) => [...new Set(orders.map(order => safeGetStatusText(order.status, order.type)))],
      getFilterValue: (order) => safeGetStatusText(order.status, order.type),
      toCSV: (order) => safeGetStatusText(order.status, order.type),
    },
    {
      key: 'priority',
      label: 'ระดับความสำคัญ',
      group: 'สถานะและการจัดการ',
      sortable: true,
      filterable: true,
      // Priority is a low-cardinality status → Badge, but only the urgent levels get color.
      // "ปกติ" (normal, the default for most rows) is muted plain text so it doesn't add
      // per-row noise (Badge rule: normal priority is visually quiet).
      render: (order) => {
        const label = getPriorityLabel(order.priority);
        if (order.priority === 1) return <Badge variant="danger">{label}</Badge>;
        if (order.priority === 100) return <Badge variant="warning">{label}</Badge>;
        return <span className="text-xs text-gray-400">{label}</span>;
      },
      filterOptions: (orders) => [...new Set(orders.map(order => getPriorityLabel(order.priority)))],
      getFilterValue: (order) => getPriorityLabel(order.priority),
      toCSV: (order) => getPriorityLabel(order.priority),
    },
    {
      key: 'assignedTo',
      label: 'ผู้รับผิดชอบ',
      group: 'สถานะและการจัดการ',
      sortable: true,
      filterable: true,
      render: (order) => order.assignedTo?.fullname || order.assignedTo?.username || '-',
      filterOptions: (orders) => {
        const assignees = orders.map(order =>
          order.assignedTo?.fullname || order.assignedTo?.username || '-'
        );
        return [...new Set(assignees)];
      },
      getFilterValue: (order) => order.assignedTo?.fullname || order.assignedTo?.username || '-',
      toCSV: (order) => order.assignedTo?.fullname || order.assignedTo?.username || '-',
    },
    {
      key: 'assignedTechnician',
      label: 'ผู้ล้าง',
      group: 'สถานะและการจัดการ',
      sortable: true,
      filterable: true,
      render: (order) => getAssignedTechnician(order) ?? '-',
      filterOptions: (orders) => {
        const assignees = orders
          .filter(order => getAssignedTechnician(order) !== null)
          .map(order => getAssignedTechnician(order)!);
        return [...new Set(assignees)];
      },
      getFilterValue: (order) => getAssignedTechnician(order) ?? '-',
      toCSV: (order) => getAssignedTechnician(order) ?? '',
    },
    {
      key: 'approvalTechnician',
      label: 'ผู้อนุมัติ(ฝ่ายแม่พิมพ์)',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      sortable: true,
      filterable: true,
      render: (order) => getApprovalTechnician(order) ?? '-',
      filterOptions: (orders) => {
        const assignees = orders
          .filter(order => getApprovalTechnician(order) !== null)
          .map(order => getApprovalTechnician(order)!);
        return [...new Set(assignees)];
      },
      getFilterValue: (order) => getApprovalTechnician(order) ?? '-',
      toCSV: (order) => getApprovalTechnician(order) ?? '',
    },
    {
      key: 'reporterConfirm',
      label: 'ผู้อนุมัติ(ฝ่ายผลิต)',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      sortable: true,
      filterable: true,
      render: (order) => getReporterConfirmLabel(order) ?? '-',
      filterOptions: (orders) => {
        const assignees = orders
          .filter(order => getReporterConfirmLabel(order) !== null)
          .map(order => getReporterConfirmLabel(order)!);
        return [...new Set(assignees)];
      },
      getFilterValue: (order) => getReporterConfirmLabel(order) ?? '-',
      toCSV: (order) => getReporterConfirmLabel(order) ?? '',
    },
    {
      key: 'createdAt',
      label: 'วันที่สร้าง',
      group: 'กำหนดเวลา',
      isDateColumn: true,
      sortable: true,
      filterable: true,
      render: (order) => (
        <DateLabel dateStr={order.createdAt} />
      ),
      filterOptions: (orders) => [...new Set(orders.map(order => formatShortThaiDateFromString(order.createdAt)))],
      getFilterValue: (order) => order.createdAt,
      sortFunction: (a, b) => {
        const aValue = a.createdAt;
        const bValue = b.createdAt;
        if (aValue === bValue) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        return new Date(aValue).getTime() - new Date(bValue).getTime();
      },
      toCSV: (order) => new Date(order.createdAt).toLocaleDateString('en-GB'),
    },
    {
      key: 'orderDetail',
      label: 'กำหนดส่ง',
      group: 'กำหนดเวลา',
      isDateColumn: true,
      sortable: true,
      filterable: true,
      render: (order) => {
        if (!order.orderDetail.dueDate) return '-';
        return <DateLabel dateStr={order.orderDetail.dueDate.toString()} />;
      },
      filterOptions: (orders) => {
        const dueDates = orders.map(order =>
          order.orderDetail.dueDate ? formatShortThaiDateFromString(order.orderDetail.dueDate.toString()) : '-'
        );
        return [...new Set(dueDates)];
      },
      getFilterValue: (order) => order.orderDetail.dueDate ?? null,
      sortFunction: (a, b) => {
        const aValue = a.orderDetail.dueDate;
        const bValue = b.orderDetail.dueDate;
        if (aValue === bValue) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        return new Date(aValue).getTime() - new Date(bValue).getTime();
      },
      toCSV: (order) => {
        if (!order.orderDetail.dueDate) return '';
        return new Date(order.orderDetail.dueDate).toLocaleDateString('en-GB');
      },
    },
    {
      key: 'operationDate',
      label: 'วันที่ล้าง',
      group: 'กำหนดเวลา',
      isDateColumn: true,
      sortable: true,
      filterable: true,
      render: (order) => {
        const completedDate = getOperationDate(order);
        return completedDate ? <DateLabel dateStr={completedDate.toString()} /> : '-';
      },
      filterOptions: (orders) => [...new Set(orders.map(order => getOperationDateString(order)))],
      getFilterValue: (order) => getOperationDate(order),
      sortFunction: (a, b) => {
        const aValue = getOperationDate(a);
        const bValue = getOperationDate(b);
        if (aValue === bValue) return 0;
        if (aValue == null) return -1;
        if (bValue == null) return 1;
        return new Date(aValue).getTime() - new Date(bValue).getTime();
      },
      toCSV: (order) => {
        const date = getOperationDate(order);
        return date 
          ? date.toLocaleDateString('en-GB') 
          : '';
      },
    },
    {
      key: 'supervisorReviewDate',
      label: 'วันที่ฝ่ายแม่พิมพ์ตรวจสอบ',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      isDateColumn: true,
      sortable: true,
      filterable: true,
      render: (order) => {
        const supervisorReviewDate = getSupervisorReviewDate(order);
        return supervisorReviewDate ? <DateLabel dateStr={supervisorReviewDate.toString()} /> : '-';
      },
      filterOptions: (orders) => [...new Set(orders.map(order => getSupervisorReviewDateString(order)))],
      getFilterValue: (order) => getSupervisorReviewDate(order) ?? null,
      sortFunction: (a, b) => {
        const aValue = getSupervisorReviewDate(a);
        const bValue = getSupervisorReviewDate(b);
        if (aValue === bValue) return 0;
        if (aValue == null) return -1;
        if (bValue == null) return 1;
        return new Date(aValue).getTime() - new Date(bValue).getTime();
      },
      toCSV: (order) => {
        const date = getSupervisorReviewDate(order);
        return date ? date.toLocaleDateString('en-GB') : '';
      },
    },
    {
      key: 'reporterConfirmationDate',
      label: 'วันที่ฝ่ายผลิตตรวจสอบ',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      isDateColumn: true,
      sortable: true,
      filterable: true,
      render: (order) => {
        const completedDate = getReporterConfirmationDate(order);
        return completedDate ? <DateLabel dateStr={completedDate.toString()} /> : '-';
      },
      filterOptions: (orders) => [...new Set(orders.map(order => getReporterConfirmationDateString(order)))],
      getFilterValue: (order) => getReporterConfirmationDate(order),
      sortFunction: (a, b) => {
        const aValue = getReporterConfirmationDate(a);
        const bValue = getReporterConfirmationDate(b);
        if (aValue === bValue) return 0;
        if (aValue == null) return -1;
        if (bValue == null) return 1;
        return new Date(aValue).getTime() - new Date(bValue).getTime();
      },
      toCSV: (order) => {
        const date = getReporterConfirmationDate(order);
        return date ? date.toLocaleDateString('en-GB') : '';
      },
    },
    {
      key: 'operationDuration',
      label: 'เวลาที่ใช้ล้าง (นาที)',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      sortable: true,
      filterable: false,
      render: (order) => getOperationDurationString(order) ?? '-',
      filterOptions: (orders) => [...new Set(orders
          .filter(order => getOperationDuration(order) !== null)
          .map(order => getOperationDurationString(order)!))],
      getFilterValue: (order) => getOperationDurationString(order) ?? '-',
      sortFunction: (a, b) => {
        const aValue = getOperationDuration(a);
        const bValue = getOperationDuration(b);
        if (aValue === bValue) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        return aValue - bValue;
      },
      toCSV: (order) => getOperationDurationString(order) ?? '',
    },
    {
      key: 'shiftCompleted',
      label: 'กะที่จะผลิต',
      group: 'กำหนดเวลา',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          {order.orderDetail.shiftCompleted ? `กะ ${order.orderDetail.shiftCompleted}` : '-'}
        </div>
      ),
      filterOptions: (orders) => [...new Set(
        orders
          .filter(order => order.orderDetail.shiftCompleted)
          .map(order => {
            return order.orderDetail.shiftCompleted ? `กะ ${order.orderDetail.shiftCompleted}` : '-'
          })
      )],
      getFilterValue: (order) => order.orderDetail.shiftCompleted ?? '-',
      sortFunction: (a, b) => {
        return (a.orderDetail.shiftCompleted ?? '').localeCompare(b.orderDetail.shiftCompleted ?? '');
      },
      toCSV: (order) => order.orderDetail.shiftCompleted ?? '',
    },
    {
      key: 'machineNumber',
      label: 'หมายเลขเครื่อง',
      group: 'ข้อมูลพื้นฐาน',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          {getMachineNumber(order) ?? '-'}
        </div>
      ),
      filterOptions: (orders) => [...new Set(
        orders
          .filter(order => getMachineNumber(order) !== null)
          .map(order => {
            return getMachineNumber(order)!
          })
      )],
      getFilterValue: (order) => getMachineNumber(order) ?? '-',
      toCSV: (order) => getMachineNumber(order) ?? '',
    },
    {
      key: 'rejectedReason',
      label: 'สาเหตุที่ไม่อนุมัติ',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          {getRejectedReason(order) ?? '-'}
        </div>
      ),
      filterOptions: (orders) => [...new Set(
        orders
          .filter(order => getRejectedReason(order) !== null)
          .map(order => {
            return getRejectedReason(order)!
          })
      )],
      getFilterValue: (order) => getRejectedReason(order) ?? '-',
      sortFunction: (a, b) => {
        return (getRejectedReason(a) ?? '').localeCompare(getRejectedReason(b) ?? '');
      },
      toCSV: (order) => `"${getRejectedReason(order) ?? ''}"`,
    },
    {
      key: 'rejectedBy',
      label: 'ผู้ที่ไม่อนุมัติ',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          {getRejectedPerson(order) ?? '-'}
        </div>
      ),
      filterOptions: (orders) => [...new Set(
        orders
          .filter(order => getRejectedPerson(order) !== null)
          .map(order => {
            return getRejectedPerson(order)!
          })
      )],
      getFilterValue: (order) => getRejectedPerson(order) ?? '-',
      sortFunction: (a, b) => {
        return (getRejectedPerson(a) ?? '').localeCompare(getRejectedPerson(b) ?? '');
      },
      toCSV: (order) => `"${getRejectedPerson(order) ?? ''}"`,
    },
    {
      key: 'rejectedState',
      label: 'ขั้นตอนที่ไม่อนุมัติ',
      group: 'สำหรับใบงานที่ปิดแล้ว',
      sortable: true,
      filterable: true,
      render: (order) => (
        <div>
          {getRejectedState(order, safeGetStatusText) ?? '-'}
        </div>
      ),
      filterOptions: (orders) => [...new Set(
        orders
          .filter(order => getRejectedState(order, safeGetStatusText) !== null)
          .map(order => {
            return getRejectedState(order, safeGetStatusText)!
          })
      )],
      getFilterValue: (order) => getRejectedState(order, safeGetStatusText) ?? '-',
      sortFunction: (a, b) => {
        return (getRejectedState(a, safeGetStatusText) ?? '').localeCompare(getRejectedState(b, safeGetStatusText) ?? '');
      },
      toCSV: (order) => `"${getRejectedState(order, safeGetStatusText) ?? ''}"`,
    },
    {
      key: 'actions',
      label: 'Action',
      group: 'การดำเนินการ',
      sortable: false,
      render: (order) => (
        <ActionCell id={order.id.toString()} prefixUrl='mold-orders' />
      ),
    },
  ];

  // Apply company-specific column configuration if companyCode is provided
  if (companyCode) {
    return applyMoldOrderColumnConfiguration(allColumns, companyCode);
  }

  return allColumns;
};

// Helper function to check if a column is a date column
export const isDateColumn = (columnKey: string, isDateColumn?: boolean): boolean => {
  return Boolean(isDateColumn) || columnKey === 'createdAt' || columnKey === 'orderDetail';
};

// Update this function to handle optional group property
export const getMoldOrdersColumnGroups = (params?: {
  statusFilterable?: boolean;
  getStatusText?: (status: MoldOrderStatus, type?: MoldOrderType) => string;
  getMoldOrderTypeLabel?: (type: MoldOrderType) => string;
  companyCode?: ClientCompanyCode;
}) => {
  const columns = getMoldOrdersTableColumns(params);
  const DEFAULT_GROUP = 'อื่นๆ'; // Default group name for columns without a group

  // Assign default group to columns without a group
  const columnsWithGroup = columns.map(column => ({
    ...column,
    group: column.group || DEFAULT_GROUP
  }));

  // Get unique group names
  const groupNames = [...new Set(columnsWithGroup.map(column => column.group))];

  // Create groups with their respective columns
  return groupNames.map(groupName => ({
    name: groupName,
    columns: columnsWithGroup
      .filter(column => column.group === groupName)
      .map(column => column.key.toString())
  }));
};

// Generate column groups for mold tables based on available columns
export const getMoldColumnGroups = (params?: {
  getStatusText?: (mold: Mold) => string;
  companyCode?: ClientCompanyCode;
}) => {
  const columns = getMoldTableColumns(params);
  const DEFAULT_GROUP = 'อื่นๆ'; // Default group name for columns without a group

  // Assign default group to columns without a group
  const columnsWithGroup = columns.map((column: GenericColumn<Mold>) => ({
    ...column,
    group: column.group || DEFAULT_GROUP
  }));

  // Get unique group names - ensure they're strings
  const groupNames = [...new Set(columnsWithGroup.map((column: GenericColumn<Mold>) => column.group))].filter(Boolean) as string[];

  // Create groups with their respective columns
  return groupNames.map(groupName => ({
    name: groupName as string,
    columns: columnsWithGroup
      .filter((column: GenericColumn<Mold>) => column.group === groupName)
      .map((column: GenericColumn<Mold>) => column.key.toString())
  }));
};
